import React, { useMemo } from "react";

import { format } from "date-fns";
import { useHistory } from "react-router-dom";

import { ClientId } from "@fartherfinance/frontend/api/Types";

import { toSortable } from "../../../Accounts/Holdings/Components/BreakdownTable/HoldingsTable";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import Message from "@src/sharedComponents/Message/Message";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

import useFindPortfolioPlan from "./useFindPortfolioPlan";

const Columns = [
  "Name",
  "Type",
  "Tax Status",
  "Assigned to Accounts",
  "Trading Groups Applied",
  "Created",
] as const;
type Columns = typeof Columns[number];

type props = {
  clientId: ClientId;
};

const PortfolioPlanTable: React.FC<props> = ({ clientId }) => {
  const history = useHistory();

  const portfolioPlans = useFindPortfolioPlan(clientId);

  const rows: Row<Columns, Cell>[] = useMemo(() => {
    return (portfolioPlans.data ?? []).map(
      (x): Row<Columns, Cell> => ({
        key: x.planId,
        Name: x.name,
        Type: x.type,
        "Tax Status": x.taxStatus,
        "Assigned to Accounts": {
          value: x.accountsAppliedCount.toLocaleString("en-US"),
          fullValue: toSortable(x.accountsAppliedCount),
        },
        "Trading Groups Applied": {
          value: x.assignmentsAppliedCount.toLocaleString("en-US"),
          fullValue: toSortable(x.assignmentsAppliedCount),
        },
        Created: {
          value: format(x.createdAt, "M/d/yyyy"),
          fullValue: x.createdAt.toISOString(),
        },
        onClick: () => {
          history.push(
            `/Client/${clientId}/${InvestmentsPath}/PortfoliosV2/${x.planId}/Accounts`
          );
        },
      })
    );
  }, [portfolioPlans.data, history, clientId]);

  if (portfolioPlans.hasError) {
    let errorMessage = "Something went wrong, please try again later";
    if (portfolioPlans.error instanceof Error) {
      errorMessage = portfolioPlans.error.message;
    }

    return <Message text={errorMessage} />;
  }

  if (portfolioPlans.isLoading) {
    return (
      <FullDataTable
        key="loading"
        isLoading={portfolioPlans.isLoading}
        columns={Columns}
        rows={Array.from({ length: 10 }).map((_, i) => ({
          key: i.toString(),
          Name: { value: <Skeleton />, fullValue: "" },
          Type: { value: <Skeleton />, fullValue: "" },
          "Tax Status": { value: <Skeleton />, fullValue: "" },
          "Assigned to Accounts": { value: <Skeleton />, fullValue: "" },
          "Trading Groups Applied": { value: <Skeleton />, fullValue: "" },
          Created: { value: <Skeleton />, fullValue: "" },
        }))}
        emptyCell={<Skeleton />}
      />
    );
  }

  return (
    <FullDataTable
      key="loaded"
      isLoading={portfolioPlans.isLoading}
      columns={Columns}
      rows={rows}
      emptyCell={<Skeleton />}
    />
  );
};

export default PortfolioPlanTable;
